import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { esignIcons } from "../../components/Esign/icons";
import Loader from "../../components/Loader";
import { esignToolApi } from "../../services/esignApi";

const tableCellSx = {
  color: "#5C5C5C",
  fontSize: "15px",
  fontWeight: 400,
  whiteSpace: "nowrap",
};

const headerCellSx = {
  background: "#F5F5F5",
  color: "#1D1D1D",
  fontSize: "15px",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const getFirstValue = (row, keys, fallback = "-") => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== "") {
      return row[key];
    }
  }
  return fallback;
};

const getArchiveRows = (response) => {
  if (Array.isArray(response)) return response;
  const candidates = [
    response?.data,
    response?.results,
    response?.data?.data,
    response?.results?.data,
    response?.archive_pdf_data,
    response?.data?.archive_pdf_data,
    response?.results?.archive_pdf_data,
    response?.records,
    response?.results?.records,
  ];
  return candidates.find(Array.isArray) || [];
};

const getArchiveCount = (response, rowsLength) =>
  Number(
    response?.count ??
      response?.total_records ??
      response?.total ??
      response?.totalEntries ??
      response?.results?.count ??
      response?.results?.total_records ??
      response?.data?.count ??
      rowsLength
  );

const getDownloadUrl = (response) =>
  getFirstValue(response, ["url", "download_url", "file_url"], "") ||
  getFirstValue(response?.data, ["url", "download_url", "file_url"], "") ||
  getFirstValue(response?.results, ["url", "download_url", "file_url"], "");

const formatDisplayDate = (value) => {
  if (!value) return "-";
  const date = dayjs(value);
  return date.isValid() ? date.format("MM-DD-YYYY") : value;
};

const isTruthyFlag = (value) => value === true || value === "true" || value === 1 || value === "1";
const isFalsyFlag = (value) => value === false || value === "false" || value === 0 || value === "0";

const Archive = () => {
  const { open } = useOutletContext();
  const { profileData } = useSelector((state) => state.profile);
  const [archiveRows, setArchiveRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [tableLoader, setTableLoader] = useState(false);
  const [loader, setLoader] = useState(false);
  const [archiveStatusByUuid, setArchiveStatusByUuid] = useState({});

  const hasArchiveAccess = isTruthyFlag(
    profileData?.archive_access ??
      profileData?.data?.archive_access ??
      profileData?.profile?.archive_access
  );

  const formattedDateRange = useMemo(() => {
    if (!startDate && !endDate) return "";
    const start = startDate ? dayjs(startDate).format("DD MMM YYYY") : "Start";
    const end = endDate ? dayjs(endDate).format("DD MMM YYYY") : "End";
    return `${start} - ${end}`;
  }, [startDate, endDate]);

  const fetchArchiveData = useCallback(async () => {
    if (!hasArchiveAccess) {
      setArchiveRows([]);
      setTotalEntries(0);
      setTableLoader(false);
      return;
    }

    setTableLoader(true);
    try {
      const response = await esignToolApi.fetchArchivePdfData({
        page: page + 1,
        pageSize: rowsPerPage,
        search: appliedSearch,
        startDate: appliedStartDate ? dayjs(appliedStartDate).format("YYYY-MM-DD") : "",
        endDate: appliedEndDate ? dayjs(appliedEndDate).format("YYYY-MM-DD") : "",
      });
      const rows = getArchiveRows(response);
      setArchiveRows(rows);
      setTotalEntries(getArchiveCount(response, rows.length));
    } catch (error) {
      console.error("Error fetching archive data:", error);
      setArchiveRows([]);
      setTotalEntries(0);
    } finally {
      setTableLoader(false);
    }
  }, [hasArchiveAccess, page, rowsPerPage, appliedSearch, appliedStartDate, appliedEndDate]);

  useEffect(() => {
    fetchArchiveData();
  }, [fetchArchiveData]);

  const handleApply = () => {
    setPage(0);
    setAppliedSearch(searchText.trim());
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  };

  const handleReset = () => {
    setSearchText("");
    setAppliedSearch("");
    setStartDate(null);
    setEndDate(null);
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setPage(0);
  };

  const handleRequestArchive = async (row) => {
    if (!hasArchiveAccess) return;

    const uuid = getFirstValue(row, ["uuid_no", "uuid", "esign_uuid", "request_id"], "");
    if (!uuid) {
      toast.warning("UUID not found for this document");
      return;
    }

    setLoader(true);
    try {
      const response = await esignToolApi.requestArchivePdf(uuid);
      if (response?.status === false) {
        toast.warning(response?.message || "Archive request failed");
        return;
      }
      toast.success(response?.message || "Archive request submitted");
      fetchArchiveData();
    } catch (error) {
      console.error("Error requesting archive PDF:", error);
    } finally {
      setLoader(false);
    }
  };

  const handleCheckStatus = async (row) => {
    if (!hasArchiveAccess) return;

    const uuid = getFirstValue(row, ["uuid_no", "uuid", "esign_uuid", "request_id"], "");
    if (!uuid) {
      toast.warning("UUID not found for this document");
      return;
    }

    setLoader(true);
    try {
      const response = await esignToolApi.checkArchivePdfStatus(uuid);
      const message = response?.data?.message || response?.message;
      if (response?.status === false) {
        toast.warning(message || "Archive PDF is not ready yet");
        return;
      }
      setArchiveStatusByUuid((prev) => ({
        ...prev,
        [uuid]: response?.data || {},
      }));
      toast.success(message || "Archive status checked");
      fetchArchiveData();
    } catch (error) {
      console.error("Error checking archive PDF status:", error);
    } finally {
      setLoader(false);
    }
  };

  const handleDownload = async (row) => {
    if (!hasArchiveAccess) return;

    const uuid = getFirstValue(row, ["uuid_no", "uuid", "esign_uuid", "request_id"], "");
    if (!uuid) {
      toast.warning("UUID not found for this document");
      return;
    }

    setLoader(true);
    try {
      const response = await esignToolApi.getArchiveDownloadUrl(uuid);
      const downloadUrl = getDownloadUrl(response);
      if (!downloadUrl) {
        toast.warning(response?.message || "Download URL not available yet");
        return;
      }
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.download = "";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (error) {
      console.error("Error downloading archive PDF:", error);
    } finally {
      setLoader(false);
    }
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 3,
        width: open ? "calc(100% - 240px)" : "calc(100% - 100px)",
        marginLeft: open ? "240px" : "100px",
        transition: "margin 0.3s ease",
        height: "100vh",
        display: "flex",
        background: "#f9f9f9",
      }}
    >
      <Box
        mt={10}
        sx={{
          borderRadius: "12px",
          background: "#fff",
          width: "100%",
          height: "calc(100% - 73px)",
          marginLeft: open ? 0 : "1em",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, p: "15px 21px", flexWrap: "wrap" }}>
          <Typography noWrap sx={{ fontSize: "20px", fontWeight: 500, color: "#1D1D1D" }}>
            Archive
          </Typography>

          {hasArchiveAccess && <Box sx={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <TextField
              placeholder="Search..."
              variant="outlined"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") handleApply();
              }}
              sx={{
                borderRadius: "10px",
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": { borderColor: "#E8E8E9" },
                  "&.Mui-focused fieldset": { borderColor: "#E8E8E9" },
                },
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">{esignIcons.searchIcon}</InputAdornment>,
                sx: {
                  borderRadius: "10px",
                  width: { xs: "100%", sm: 350, md: 410 },
                  height: 40,
                  fontSize: "16px",
                  fontWeight: 500,
                },
              }}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label=""
                value={startDate}
                maxDate={endDate || undefined}
                onChange={setStartDate}
                format="DD MMM YYYY"
                slotProps={{
                  textField: {
                    placeholder: formattedDateRange || "Start date",
                    sx: datePickerSx,
                  },
                }}
              />
              <DatePicker
                label=""
                value={endDate}
                minDate={startDate || undefined}
                onChange={setEndDate}
                format="DD MMM YYYY"
                slotProps={{
                  textField: {
                    placeholder: formattedDateRange || "End date",
                    sx: datePickerSx,
                  },
                }}
              />
            </LocalizationProvider>

            <Button variant="contained" sx={secondaryButtonSx} onClick={handleReset}>
              Reset
            </Button>
            <Button variant="contained" sx={primaryButtonSx} onClick={handleApply}>
              Apply
            </Button>
          </Box>}
        </Box>

        <Divider />
        {!hasArchiveAccess && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative", left: "50%", top: "34%", transform: "translate(-50%, -50%)"  }}>
            <img src={`${process.env.PUBLIC_URL}/images/not_found.png`} alt="Premium" style={{ height: 166 }} />
            <Typography variant="caption" sx={{ fontSize: "18px", fontWeight: "600", color: "#000000" }}>
                Administrator Permission Required
            </Typography>
            <Typography variant="caption" sx={{ fontSize: "14px", fontWeight: "400", color: "#000000", textAlign: "center"}}>
                You don't have access to the archive section. <br/>Please contact meon administrator to restore this PDF.
            </Typography>
            <Button variant="contained" sx={{ mt: "25px", width: '239px', height: 40, color: "#FFFFFF", background:"#F4F4F4", textTransform: "none", fontSize: "14px", borderRadius: "12px", border: "1px solid #E5E5E5", boxShadow: "none", fontWeight: "500", display: "flex", alignItems:"center", gap:1, "&:disabled": { background: "#F4F4F4", color: "#1D1D1D" } }} disabled >
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.25 3.75L5.57064 6.19811C7.1635 7.10063 7.8365 7.10063 9.42936 6.19811L13.75 3.75" stroke="#1D1D1D" stroke-width="1.2" stroke-linejoin="round"/>
                <path d="M1.25985 8.42226C1.30071 10.3382 1.32114 11.2962 2.0281 12.0058C2.73505 12.7155 3.71896 12.7402 5.68677 12.7897C6.89957 12.8201 8.10043 12.8201 9.31324 12.7897C11.281 12.7402 12.265 12.7155 12.9719 12.0058C13.6789 11.2962 13.6993 10.3382 13.7401 8.42226C13.7533 7.80621 13.7533 7.1938 13.7401 6.57775C13.6993 4.66179 13.6789 3.70381 12.9719 2.99416C12.265 2.28452 11.281 2.2598 9.31324 2.21036C8.10043 2.17988 6.89957 2.17988 5.68676 2.21035C3.71896 2.25979 2.73505 2.28451 2.0281 2.99415C1.32114 3.7038 1.30071 4.66178 1.25985 6.57774C1.24672 7.19379 1.24672 7.80621 1.25985 8.42226Z" stroke="#1D1D1D" stroke-width="1.2" stroke-linejoin="round"/>
                </svg>
                support@meon.co.in
            </Button>
          </Box>
        )}
        {hasArchiveAccess && (
          <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none", flex: 1, borderRadius: 0, display: "flex", flexDirection: "column" }}>
            <TableContainer sx={{ flex: 1 }}>
              <Table stickyHeader sx={{ minWidth: 1260 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headerCellSx, pl: "30px" }}>S. No.</TableCell>
                    <TableCell sx={headerCellSx}>UID</TableCell>
                    <TableCell sx={headerCellSx}>Email Address</TableCell>
                    <TableCell sx={headerCellSx}>Mobile number</TableCell>
                    <TableCell sx={headerCellSx}>Archived on</TableCell>
                    <TableCell sx={headerCellSx} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableLoader ? (
                    [...Array(Math.min(rowsPerPage, 10))].map((_, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ pl: "30px" }}><Skeleton width={36} /></TableCell>
                        <TableCell><Skeleton width={130} /></TableCell>
                        <TableCell><Skeleton width={180} /></TableCell>
                        <TableCell><Skeleton width={130} /></TableCell>
                        <TableCell><Skeleton width={120} /></TableCell>
                        <TableCell><Skeleton width={360} /></TableCell>
                      </TableRow>
                    ))
                  ) : archiveRows.length > 0 ? (
                    archiveRows.map((row, index) => {
                      const uuid = getFirstValue(row, ["uuid_no", "uuid", "esign_uuid", "request_id"], "");
                      const checkedStatus = archiveStatusByUuid[uuid] || {};
                      const downloadReady = isTruthyFlag(checkedStatus?.download_ready ?? row?.download_ready);
                      const canRestore =
                        isTruthyFlag(row?.deep_archive) &&
                        isFalsyFlag(row?.restore_requested) &&
                        isFalsyFlag(row?.download_ready);

                      return (
                        <TableRow key={uuid || index} sx={{ "&:hover": { backgroundColor: "#FAFAFA" } }}>
                          <TableCell sx={{ ...tableCellSx, pl: "30px" }}>
                            {(page * rowsPerPage + index + 1).toString().padStart(2, "0")}
                          </TableCell>
                          <TableCell sx={tableCellSx}>
                            <Tooltip title={uuid || ""}>
                              <span>{uuid || "-"}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={tableCellSx}>{getFirstValue(row, ["email", "email_address", "signer_email"])}</TableCell>
                          <TableCell sx={tableCellSx}>{getFirstValue(row, ["mobile", "mobile_number", "phone", "phone_number"])}</TableCell>
                          <TableCell sx={tableCellSx}>
                            {formatDisplayDate(getFirstValue(row, ["archived_on", "archive_date", "archived_at", "created_at"], ""))}
                          </TableCell>
                          <TableCell sx={{ ...tableCellSx, minWidth: 400 }} align="center">
                            <Box sx={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                              <Button
                                variant="outlined"
                                sx={actionButtonSx}
                                disabled={!uuid || !canRestore}
                                onClick={() => handleRequestArchive(row)}
                              >
                                {row?.restore_requested ? "Requested" : "Restore"}
                              </Button>
                              <Button variant="outlined" sx={actionButtonSx} disabled={!uuid} onClick={() => handleCheckStatus(row)}>
                                Check Status
                              </Button>
                              <Button
                                variant="outlined"
                                sx={disabledActionButtonSx}
                                disabled={!uuid || !downloadReady}
                                onClick={() => handleDownload(row)}
                              >
                                Download
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6, color: "#5C5C5C" }}>
                        No archive documents found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 20]}
              component="div"
              count={totalEntries}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        )}
      </Box>
      {loader && <Loader />}
    </Box>
  );
};

const datePickerSx = {
  width: { xs: "100%", sm: 190 },
  "& .MuiInputBase-root": {
    height: 40,
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: 500,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E8E8E9",
  },
};

const primaryButtonSx = {
  minWidth: 100,
  height: 40,
  color: "#fff",
  background: "#185CB7",
  textTransform: "none",
  fontSize: "15px",
  borderRadius: "8px",
  fontWeight: 500,
  "&:hover": { background: "#154f9d" },
};

const secondaryButtonSx = {
  minWidth: 88,
  height: 40,
  color: "#1D1D1D",
  background: "#fff",
  textTransform: "none",
  fontSize: "15px",
  borderRadius: "8px",
  border: "1px solid #D2D2D2",
  fontWeight: 500,
  "&:hover": { background: "#F8F8F8" },
};

const actionButtonSx = {
  minWidth: 132,
  height: 36,
  borderRadius: "9px",
  borderColor: "#185CB7",
  color: "#185CB7",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 500,
  "&:disabled": {
    borderColor: "#DCDCDC",
    color: "#9A9A9A",
    background: "#F7F7F7",
  },
};

const disabledActionButtonSx = {
  ...actionButtonSx,
  "&:disabled": {
    borderColor: "#DCDCDC",
    color: "#9A9A9A",
    background: "#F7F7F7",
  },
};

export default Archive;
